import { useEffect, useMemo, useRef } from "react";
import {
  buildPendoIdentityFromUser,
  cleanupPendo,
  getPendoConfig,
  initPendo,
  installAideRejectionHandler,
  isParishSoftEmbedSession,
  loadAideLoader,
  PARISHSOFT_HOST_APP,
  setPendoRuntimeConfig,
} from "./pendo";
import { trackFeatureEvent, trackGuideEvent, trackPageView } from "./pendo-tracking";
import { api } from "../api/api";

export function usePendoBootstrap({ user, hasLoginToken, history, groupsTotalCount, peopleTotalCount }) {
  const lastIdentityRef = useRef("");
  const config = useMemo(() => getPendoConfig(), []);

  // Fetch Pendo config from backend on first mount and apply it
  useEffect(() => {
    api.settings.getPendoConfig().then((result) => {
      if (result?.success) {
        setPendoRuntimeConfig({
          apiKey: result?.apiKey,
          aideLoaderUrl: result?.aideLoaderUrl,
          appName: result?.appName,
          enabled: result?.enabled,
        });
      }
    });
  }, []);

  useEffect(() => {
    return installAideRejectionHandler(window);
  }, []);

  useEffect(() => {
    if (!config.enabled || !hasLoginToken || !user?.loaded) return;
    loadAideLoader();
  }, [config.enabled, hasLoginToken, user?.loaded]);

  useEffect(() => {
    if (!config.enabled) return;
    if (!hasLoginToken || !user?.loaded) return;

    const identity = buildPendoIdentityFromUser(user, config, {
      totalGroups: groupsTotalCount,
      totalMembers: peopleTotalCount,
      enabledChannels: user?.enabledChannels ?? user?.EnabledChannels,
      productId: user?.productId ?? user?.ProductId,
    });
    const identityKey = `${identity?.account?.id}:${identity?.visitor?.id}`;
    const shouldTrackIdentity = identityKey !== lastIdentityRef.current;

    initPendo(identity?.account, identity?.visitor);
    if (shouldTrackIdentity) {
      lastIdentityRef.current = identityKey;
    }
  }, [
    config,
    hasLoginToken,
    user?.loaded,
    user?.customerId,
    user?.customerName,
    user?.userId,
    user?.firstName,
    user?.lastName,
    user?.enabledChannels,
    user?.EnabledChannels,
    user?.productId,
    user?.ProductId,
    groupsTotalCount,
    peopleTotalCount,
  ]);

  useEffect(() => {
    if (!config.enabled || !hasLoginToken || !user?.loaded || !history) return;

    const initialPath = history.location?.pathname || window.location.pathname;
    trackPageView(initialPath, { tracking_env: config.environment });

    const unlisten = history.listen((location) => {
      trackPageView(location.pathname, { tracking_env: config.environment });
    });

    return () => {
      if (typeof unlisten === "function") {
        unlisten();
      }
    };
  }, [config.enabled, config.environment, hasLoginToken, user?.loaded, history]);
}

export function usePendoCleanup(hasLoginToken) {
  useEffect(() => {
    if (hasLoginToken) return;
    // A ParishSoft embed session has no redux login token but is still an
    // identified Pendo session — cleaning up here would disable in-iframe tracking.
    if (isParishSoftEmbedSession()) return;
    cleanupPendo();
  }, [hasLoginToken]);
}

/**
 * Bootstraps Pendo inside the HGS iframe embedded by ParishSoft.
 *
 * The embed authenticates with ParishSoftToken (localStorage) and never populates the
 * redux user domain, so usePendoBootstrap never fires there. This hook resolves the
 * visitor/account identity server-side from the ParishSoft session token and initializes
 * the Pendo agent already loaded by the snippet in public/index.html.
 */
export function usePendoParishSoftBootstrap({ history } = {}) {
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    if (!isParishSoftEmbedSession()) return;

    bootstrappedRef.current = true;
    let cancelled = false;

    async function bootstrap() {
      const configResult = await api.settings.getPendoConfig();
      if (cancelled) return;

      if (configResult?.success) {
        setPendoRuntimeConfig({
          apiKey: configResult.apiKey,
          aideLoaderUrl: configResult.aideLoaderUrl,
          appName: configResult.appName,
          enabled: configResult.enabled,
        });
      }

      const config = getPendoConfig();
      if (!config.enabled) return;

      const identityResult = await api.parishSoft.getPendoIdentity();
      if (cancelled || !identityResult?.success || !identityResult.userId) return;
      const { account, visitor } = buildPendoIdentityFromUser(identityResult, config);
      const embedAccount = { ...account, host_app: PARISHSOFT_HOST_APP };
      const embedVisitor = { ...visitor, host_app: PARISHSOFT_HOST_APP, embedded: true };

      initPendo(embedAccount, embedVisitor);
      trackPageView(window.location.pathname, {
        tracking_env: config.environment,
        host_app: PARISHSOFT_HOST_APP,
        embedded: true,
      });
    }

    bootstrap();

    return () => {
      cancelled = true;
      // Reset the guard on cleanup so React 18 StrictMode's dev-mode
      // mount -> cleanup -> remount cycle restarts bootstrap() on the second
      // mount instead of permanently short-circuiting it. Without this, the
      // only real bootstrap attempt gets cancelled mid-flight (its awaits
      // resolve after `cancelled` is already true) and pendo.initialize()
      // is never called inside the ParishSoft iframe.
      bootstrappedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!history || !isParishSoftEmbedSession()) return;

    const unlisten = history.listen((location) => {
      trackPageView(location.pathname, {
        tracking_env: getPendoConfig().environment,
        host_app: PARISHSOFT_HOST_APP,
        embedded: true,
      });
    });

    return () => {
      if (typeof unlisten === "function") unlisten();
    };
  }, [history]);
}

export function usePendoTracker(defaultDomain = "app") {
  function trackFeature(feature, action, metadata = {}) {
    return trackFeatureEvent(defaultDomain, feature, action, metadata);
  }

  function trackGuide(guideName, action = "viewed", metadata = {}) {
    return trackGuideEvent(guideName, action, metadata);
  }

  return { trackFeature, trackGuide };
}
